import { useCallback, useEffect, useRef, useState } from "react";
import { useUnmount } from "react-use";
import { Call, CallCreateResponse, CallStatus } from "@scrile/api-provider/dist/api/CallsProvider";
import { WebRTCConnection } from "@scrile/api-provider/dist/api/WebRTCProvider";
import { StreamingHelper } from "@scrile/streaming-client/build";
import { TrackOptions } from "@scrile/streaming-client/build/ConsumerClient";
import { Subscription } from "apollo-client/util/Observable";
import { addTracksToStream, getVideoTracks, removeTracksFromStream } from "lib/mediaStreamHelpers";
import { onNetworkRecovery } from "lib/networkRecovery";
import providers, { getAuthToken } from "lib/providers";
import useAuthUser from "./useAuthUser";
import useConnectionReconnect from "./useConnectionReconnect";
import useMediaConstraints from "./useMediaConstraints";
import useStreamHelper from "./useStreamHelper";
import { SendDataEvent } from "../const";

export type CallPhase = "ringing" | "connecting" | "connected" | "ended";

const TERMINAL_CALL_STATUSES = [CallStatus.ENDED, CallStatus.CANCELLED, CallStatus.DECLINED];

function endReasonFromStatus(status: CallStatus): string {
  if (status === CallStatus.DECLINED) return "declined";
  if (status === CallStatus.CANCELLED) return "cancelled";
  return "ended";
}

interface UseCallControllerOptions {
  callId: string;
  /** WebRTCConnection passed via route state (caller has it from callCreate, callee from incomingCall) */
  initialConnection?: WebRTCConnection;
  /** True if this user initiated the call */
  isCaller: boolean;
  voiceOnly?: boolean;
}

// Module-scope monotonic counter so consecutive useCallController instances
// appear as #1, #2, #3, ... in the diagnostic dump. Lets a spurious
// unmount-then-mount in the same /call/:id session be spotted at a glance:
// instance #N's unmount line is followed milliseconds later by instance
// #N+1's mount line, same callId.
let __useCallControllerInstanceSeq = 0;

export default function useCallController({
  callId,
  initialConnection,
  isCaller,
  voiceOnly,
}: UseCallControllerOptions) {
  const [callPhase, setCallPhase] = useState<CallPhase>(isCaller ? "ringing" : "connecting");
  const [call, setCall] = useState<Call | null>(null);
  const [endReason, setEndReason] = useState<string | null>(null);
  const { user } = useAuthUser();

  // Instance id assigned at first render; kept stable via useRef. Logged on
  // mount and unmount so each useCallController lifetime is a matched pair.
  const instanceIdRef = useRef<number>(0);
  if (instanceIdRef.current === 0) {
    __useCallControllerInstanceSeq += 1;
    instanceIdRef.current = __useCallControllerInstanceSeq;
    // eslint-disable-next-line no-console
    console.warn(
      `[PAGE-LIFECYCLE] useCallController MOUNT instance#${
        instanceIdRef.current
      } callId=${callId} isCaller=${isCaller} voiceOnly=${!!voiceOnly} t=${Math.round(performance.now())}ms userId=${
        user?.id ?? "null"
      }`
    );
  }

  // WebRTC
  const producerWebRTC = useRef<StreamingHelper | null>(null);
  const consumerWebRTC = useRef<StreamingHelper | null>(null);
  // State mirrors of producerWebRTC / consumerWebRTC so
  // `useConnectionReconnect` re-binds its event listeners when either is
  // rebuilt during reconnect. Refs alone would not trigger the hook's
  // useEffect. We watch BOTH transports because in 1-on-1 they often live
  // on independent transports — losing the producer (outgoing video stops)
  // is invisible to the consumer-side ICE listener and would otherwise
  // never trigger recovery.
  const [consumerHelper, setConsumerHelper] = useState<StreamingHelper | null>(null);
  const [producerHelper, setProducerHelper] = useState<StreamingHelper | null>(null);
  const connectionRef = useRef<WebRTCConnection | null>(initialConnection || null);
  const [localStream, setLocalStream] = useState<MediaStream>(new MediaStream([]));
  const [remoteStream, setRemoteStream] = useState<MediaStream>(new MediaStream([]));
  const { checkStreamForChanges } = useStreamHelper();

  const { mediaConstraints, setMediaConstraints } = useMediaConstraints({
    video: true,
    audio: true,
    videoEnabled: !voiceOnly,
    audioEnabled: true,
    shareEnabled: false,
    blurEnabled: false,
  });

  const mountedRef = useRef(true);
  // Auth user may still be resolving at mount — the findActive restore path
  // reads it through a ref after its awaits.
  const userRef = useRef(user);
  userRef.current = user;
  const statusSubRef = useRef<Subscription | null>(null);
  const endingRef = useRef(false);
  const startStreamsRef = useRef<() => Promise<void>>();
  /**
   * Guard against concurrent / duplicate invocations of `startStreams`.
   * On the callee side up to THREE effects want to kick off media:
   *   1. findActive() resolves with an already-ACCEPTED call
   *   2. the isCaller=false + callPhase="connecting" mount effect
   *   3. the callStatusUpdated subscription firing ACCEPTED
   * Without a guard each run creates a fresh `StreamingHelper`, overwrites
   * `producerWebRTC.current`, and leaks the previous producer's tracks —
   * the remote side then sees a stale producer id and stays in Connecting.
   */
  const streamsStartedRef = useRef(false);

  // Catch-all registry of every getUserMedia result. `localStream` only ever
  // holds the tracks CURRENTLY produced to the SFU — but a camera/mic toggle
  // clones a fresh device track into the producer stream (addTracksToStream)
  // and leaves the ORIGINAL getUserMedia track live yet untracked. That orphan
  // kept the OS camera/mic LED lit after leaving the call (only a page reload
  // released it). Registering every acquisition lets teardown stop them all,
  // no matter how they were cloned downstream.
  const acquiredTracksRef = useRef<MediaStreamTrack[]>([]);
  const stopAcquiredMedia = useCallback(() => {
    acquiredTracksRef.current.forEach((t) => {
      try {
        t.stop();
      } catch {
        /* ignore */
      }
    });
    acquiredTracksRef.current = [];
  }, []);

  // Fetch call data and connection if not provided. This is ALSO the
  // reload-restore path: after a page reload the router state (connection,
  // isCaller) is gone, so an in-progress call must be rejoined from
  // findActive() alone.
  const restoreFromActive = useCallback(async () => {
    {
      try {
        // The app is WebSocket-only: right after a reload every query races
        // the socket handshake ("WebSocket is closed before the connection
        // is established"). getAuthToken() blocks until the short-live token
        // arrives over the connected socket (8 s cap).
        try {
          await getAuthToken();
        } catch {
          /* fall through — the retry loop below still gets its chances */
        }
        // Small retry ladder: the very first query can still race the socket.
        let active: CallCreateResponse[] = [];
        const delays = [0, 500, 1000, 2000];
        for (let i = 0; i < delays.length; i++) {
          if (delays[i]) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((r) => setTimeout(r, delays[i]));
          }
          if (!mountedRef.current) return;
          try {
            // eslint-disable-next-line no-await-in-loop
            active = await providers.CallsProvider.findActive();
            break;
          } catch (e) {
            if (i === delays.length - 1) throw e;
          }
        }
        const found = active.find((c) => String(c.call.id) === String(callId));
        if (found && mountedRef.current) {
          setCall(found.call);
          if (!connectionRef.current) {
            connectionRef.current = found.connection;
          }
          // An already-ACCEPTED call means media should flow NOW — regardless
          // of the isCaller prop. After a reload isCaller is a default (the
          // router state is gone), and the old `!isCaller` gate left the
          // restored caller stuck on "Calling..." forever. startStreams is
          // idempotent (streamsStartedRef), so a duplicate invocation from
          // the status subscription is harmless.
          if (found.call.status === CallStatus.ACCEPTED) {
            setCallPhase("connecting");
            startStreamsRef.current?.();
          } else if (
            found.call.status === CallStatus.NEW &&
            userRef.current &&
            String(found.call.callerId) === String(userRef.current.id)
          ) {
            // Restored caller whose call hasn't been answered yet — back to
            // ringing; the status subscription drives ACCEPTED as usual.
            setCallPhase("ringing");
          }
        } else {
          // Fallback: search by id
          const result = await providers.CallsProvider.find({ data: { ids: [callId] } });
          if (result.result?.length > 0 && mountedRef.current) {
            const fetched = result.result[0];
            setCall(fetched);
            // Reload/back-navigation onto a finished call: show "Call ended"
            // instead of an eternal "Calling..." (it's not in findActive, so
            // no rejoin is possible).
            if (TERMINAL_CALL_STATUSES.includes(fetched.status)) {
              setEndReason(endReasonFromStatus(fetched.status));
              setCallPhase("ended");
            }
          }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[call] Failed to fetch call data:", e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callId]);

  useEffect(() => {
    restoreFromActive();
  }, [restoreFromActive]);

  // Reload-restore is best-effort: the retry ladder above (0/500/1000/2000ms
  // + the 8s getAuthToken cap) can be exhausted while the network is briefly
  // down (elevator, Wi-Fi handoff) on a page reload, leaving the page stuck
  // on "Connecting…" with no recovery. Re-run the restore when connectivity
  // returns, but only while we haven't actually connected yet.
  useEffect(() => {
    const unsub = onNetworkRecovery(() => {
      if (callPhaseRef.current !== "connected" && callPhaseRef.current !== "ended") {
        restoreFromActive();
      }
    });
    return unsub;
  }, [restoreFromActive]);

  // Subscribe to call status updates
  useEffect(() => {
    (async () => {
      try {
        statusSubRef.current = await providers.CallsProvider.subscribeCallStatusUpdated((updatedCall: Call) => {
          if (String(updatedCall.id) !== String(callId)) return;
          if (!mountedRef.current) return;

          setCall(updatedCall);

          if (updatedCall.status === CallStatus.ACCEPTED) {
            setCallPhase("connecting");
            startStreamsRef.current?.();
          } else if (TERMINAL_CALL_STATUSES.includes(updatedCall.status)) {
            const reason = endReasonFromStatus(updatedCall.status);
            // eslint-disable-next-line no-console
            console.warn(
              `[CALL-PHASE-ENDED] from=status-subscription callId=${callId} backendStatus=${updatedCall.status} reason=${reason}`
            );
            setEndReason(reason);
            setCallPhase("ended");
          }
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[call] Status subscription failed:", e);
      }
    })();

    return () => {
      if (statusSubRef.current) statusSubRef.current.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callId]);

  // Start producing + consuming WebRTC streams
  const startStreams = useCallback(async () => {
    if (!connectionRef.current || !mountedRef.current) return;
    // Idempotent — see `streamsStartedRef` doc. Multiple callers (findActive
    // effect, mount effect, status subscription) race to invoke this;
    // only the first one should touch WebRTC.
    if (streamsStartedRef.current) return;
    streamsStartedRef.current = true;
    const conn = connectionRef.current;

    try {
      // Get local media
      let localMedia: MediaStream;
      try {
        const constraints: MediaStreamConstraints = {
          video: mediaConstraints.videoEnabled ? mediaConstraints.video || true : false,
          audio: mediaConstraints.audioEnabled ? mediaConstraints.audio || true : false,
        };
        // getUserMedia requires at least one of audio/video to be true.
        // We force audio in that case to keep the API happy, but the
        // post-capture guard below removes the track if the user
        // actually wanted muted — see the "remove unwanted tracks"
        // block.
        if (!constraints.video && !constraints.audio) {
          constraints.audio = true;
        }
        try {
          localMedia = await navigator.mediaDevices.getUserMedia(constraints);
        } catch (constraintErr) {
          // Fallback: saved constraints may be stale (old deviceId etc.)
          // eslint-disable-next-line no-console
          console.warn("[call] Constraints failed, retrying with defaults:", constraintErr);
          localMedia = await navigator.mediaDevices.getUserMedia({
            video: mediaConstraints.videoEnabled ? true : false,
            audio: true,
          });
        }
        // Remove tracks the user didn't actually want. This is critical
        // for the canonical bug:
        //   1. user has muted mic during a previous call → localStorage
        //      persists audioEnabled=false
        //   2. new voice-only call starts with audioEnabled=false AND
        //      videoEnabled=false (voiceOnly)
        //   3. constraint becomes {video:false, audio:false}, fallback
        //      above forces audio=true (getUserMedia API requirement)
        //   4. without this guard, the captured mic track flows straight
        //      to the producer and the callee hears the muted-UI caller
        //      until they toggle Mute → Unmute → Mute, at which point
        //      toggleAudio's "disable" branch correctly stops the track
        // Mirror the existing video guard for symmetry.
        if (!mediaConstraints.videoEnabled) {
          localMedia.getVideoTracks().forEach((t) => {
            t.stop();
            localMedia.removeTrack(t);
          });
        }
        if (!mediaConstraints.audioEnabled) {
          localMedia.getAudioTracks().forEach((t) => {
            t.stop();
            localMedia.removeTrack(t);
          });
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[call] getUserMedia failed, continuing without media:", e);
        localMedia = new MediaStream([]);
      }

      // Register before any cloning so teardown can release the OS device even
      // if a later toggle clones these tracks into the producer stream.
      localMedia.getTracks().forEach((t) => acquiredTracksRef.current.push(t));

      if (!mountedRef.current) {
        localMedia.getTracks().forEach((t) => t.stop());
        return;
      }

      // Produce (send our stream)
      const producer = new StreamingHelper();
      producerWebRTC.current = producer;
      setProducerHelper(producer);
      const producedStream = await producer.produceStream(conn, localMedia);
      if (!mountedRef.current) return;
      setLocalStream(new MediaStream(producedStream.getTracks()));

      // Consume (receive remote stream)
      const consumer = new StreamingHelper();
      consumerWebRTC.current = consumer;
      setConsumerHelper(consumer);

      const consumeWithRetry = async (attempt = 0): Promise<void> => {
        if (!mountedRef.current) return;
        try {
          const remoteMedia = await consumer.consumeStream({
            connection: conn,
            tracks: { audio: false, video: false, screen: false },
            onData: (data: { event: SendDataEvent }) => {
              if (data.event === SendDataEvent.PRODUCER_FINISHED_STREAM) {
                // eslint-disable-next-line no-console
                console.warn(
                  `[CALL-PHASE-ENDED] from=peer-finished-stream callId=${callId} (remote producer signalled they hung up)`
                );
                setEndReason("ended");
                setCallPhase("ended");
              }
            },
          });
          if (!mountedRef.current) return;
          setRemoteStream(remoteMedia);
          setCallPhase("connected");
        } catch (e) {
          if (attempt < 5 && mountedRef.current) {
            // eslint-disable-next-line no-console
            console.info(`[call] Consume retry ${attempt + 1}/5 in ${(attempt + 1) * 1000}ms`);
            await new Promise((r) => setTimeout(r, (attempt + 1) * 1000));
            return consumeWithRetry(attempt + 1);
          }
          // eslint-disable-next-line no-console
          console.error("[call] Failed to consume remote stream:", e);
        }
      };

      // NOTE: previously consumer.on("close") force-ended the call. That
      // turned every brief network blip (Wi-Fi → LTE handoff, VPN switch)
      // into a permanent hang-up. We now let useConnectionReconnect own
      // the close → retry decision. The call is only marked "ended" when
      // the server confirms it via subscribeCallStatusUpdated, or the
      // remote side sends PRODUCER_FINISHED_STREAM, or all retries fail.

      // Listen for producer updates (remote toggles camera/mic)
      consumer.on("producers-updated", async (data: Required<TrackOptions>) => {
        if (!mountedRef.current) return;
        try {
          const newRemote = await consumer.consumeStream({
            connection: conn,
            tracks: { audio: data.audio, video: data.video, screen: data.screen },
          });
          if (mountedRef.current) {
            setRemoteStream(new MediaStream(newRemote.getTracks()));
          }
        } catch {
          /* ignore */
        }
      });

      await consumeWithRetry();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[call] Failed to start streams:", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaConstraints]);

  // Keep ref in sync so subscription callbacks can call latest version
  startStreamsRef.current = startStreams;

  // For callee: start streams immediately (call already accepted)
  useEffect(() => {
    if (!isCaller && connectionRef.current && callPhase === "connecting") {
      startStreams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCaller]);

  // Callee safety net: when the user landed here through a path that
  // bypassed `IncomingCallModal.handleAccept` (deep link, browser back,
  // route restore, native CallKit accept that fired before the in-page
  // modal mounted) the call stays in `status=NEW` on the backend while
  // both peers happily exchange audio/video. The
  // `endUnansweredCall` scheduler in [DefaultCallService.java] then
  // force-cancels the call at the 60-65s mark, tearing down both peers'
  // SFU transports without warning.
  //
  // We derive "I am the callee" from `call.receiverId === user.id`, NOT
  // from the `isCaller` prop. The prop comes from route state
  // (`location.state.isCaller`) which is LOST on browser restore /
  // deep-link / tab restore — exactly the scenarios this safety net
  // targets — and its state-less default (`false` in PageDirectCall) is a
  // guess, not the real role.
  //
  // The backend's `accept()` is gated by `@RequiredCallStatus(NEW)` and
  // serialised by `@PreventDuplicate` keyed on the callId, so racing
  // with the modal's accept is safe: whichever lands first wins, the
  // other throws a validation exception which we treat as "already
  // ACCEPTED" (expected) and log at debug. Genuine errors (auth, 5xx,
  // network) are logged at warn so they surface in the diagnostic dump.
  //
  // Gated by `acceptedRef` so it only fires once per page mount even
  // when `call` is updated by the status subscription (e.g. the
  // ACCEPTED-event we just published comes right back).
  const acceptedRef = useRef(false);
  useEffect(() => {
    if (!callId || !call || !user) return;
    if (String(call.receiverId) !== String(user.id)) return;
    if (acceptedRef.current) return;
    acceptedRef.current = true;
    let cancelled = false;
    // Surface a call that is no longer joinable (cancelled/declined/ended
    // while we were registering) as "ended" instead of leaving the callee on
    // an eternal "Connecting…". Covers both the accept() response (defensive)
    // and the validation-error branch below.
    const settleIfTerminal = (status?: CallStatus) => {
      if (cancelled || !status || !TERMINAL_CALL_STATUSES.includes(status)) return false;
      setEndReason(endReasonFromStatus(status));
      setCallPhase("ended");
      return true;
    };
    (async () => {
      try {
        const accepted = await providers.CallsProvider.accept({ callId });
        if (cancelled) return;
        if (settleIfTerminal((accepted as Call | undefined)?.status)) return;
        // eslint-disable-next-line no-console
        console.info(`[call-accept] callee-mount ensured ACCEPTED callId=${callId}`);
      } catch (e) {
        if (cancelled) return;
        // ValidationException from `@RequiredCallStatus(NEW)` means the
        // modal already ACCEPTED — expected, debug only. Anything else
        // (network, 401/403, 5xx) is a real problem we want surfaced in
        // diagnosticDump.
        //
        // Backend ships the validation message as a JSON envelope. In
        // production we've now seen TWO different shapes from the same
        // backend depending on transport (REST vs GraphQL):
        //   (a) `e.response.data.errors = [{message:"..."}, ...]`
        //   (b) `e.response.data = [{message:"..."}, ...]` (array AT the root)
        // and `e.message` itself can be the JS toString of (b) — literally
        // the string "[object Object],[object Object]". The previous fix
        // only checked (a), so (b) fell through to e.message which
        // never matches the regex and we logged every legit
        // "already-accepted" as an unexpected error.
        //
        // Walk both shapes; defensive Array.isArray on every layer.
        const responseData = (e as { response?: { data?: unknown } })?.response?.data;
        const collectMessages = (val: unknown): string[] => {
          if (!val) return [];
          if (Array.isArray(val)) {
            return val.flatMap((it) => collectMessages(it));
          }
          if (typeof val === "object") {
            const obj = val as Record<string, unknown>;
            const out: string[] = [];
            if (typeof obj.message === "string") out.push(obj.message);
            if (Array.isArray(obj.errors)) out.push(...collectMessages(obj.errors));
            return out;
          }
          if (typeof val === "string") return [val];
          return [];
        };
        const messages = collectMessages(responseData);
        const messageJoined = messages.join(" | ");
        // Last resort: e.message itself. Skip "[object Object]..." patterns
        // since those carry no signal.
        const eMessage = (e as Error)?.message || "";
        const usefulEMessage = /\[object Object\]/.test(eMessage) ? "" : eMessage;
        const message = messageJoined || usefulEMessage || String(e);
        // Two unambiguous markers: the validator class name or its
        // literal message. `status.*new` deliberately omitted (loose).
        const expected = /requiredcallstatus|current.*status.*does.*not.*support|already.*accept/i.test(message);
        if (expected) {
          // "Not NEW" covers BOTH already-ACCEPTED (keep connecting) and
          // already-terminal (cancelled/declined/ended — must show ended, or
          // the callee hangs on "Connecting…" forever). The error alone can't
          // tell them apart, so re-fetch the authoritative status.
          try {
            const res = await providers.CallsProvider.find({ data: { ids: [callId] } });
            const fetched = res.result?.[0];
            if (!cancelled && fetched && settleIfTerminal(fetched.status)) return;
          } catch {
            /* best-effort — the status subscription is the other recovery path */
          }
          // eslint-disable-next-line no-console
          console.debug(`[call-accept] callee-mount already-accepted callId=${callId}`);
        } else {
          // eslint-disable-next-line no-console
          console.warn(`[call-accept] callee-mount unexpected error callId=${callId} message="${message}":`, e);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [callId, call, user]);

  // Toggle video
  const toggleVideo = useCallback(async () => {
    const newEnabled = !mediaConstraints.videoEnabled;
    setMediaConstraints((prev) => ({ ...prev, videoEnabled: newEnabled }));

    const stream = producerWebRTC.current?.webRTCData?.producer?.stream;
    if (!stream || !connectionRef.current) return;

    if (newEnabled) {
      try {
        const newMedia = await navigator.mediaDevices.getUserMedia({ video: mediaConstraints.video || true });
        newMedia.getTracks().forEach((t) => acquiredTracksRef.current.push(t));
        // closeOutTracks=true: addTracksToStream clones the track into the
        // producer stream — stop the ORIGINAL getUserMedia track so it doesn't
        // stay live holding the camera after the call ends (only the clone is
        // produced; localStream/closeAllStreams handle that one).
        addTracksToStream(stream, newMedia.getVideoTracks(), true);
        await producerWebRTC.current!.produceStream(connectionRef.current, stream);
        setLocalStream(new MediaStream(stream.getTracks()));
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[call] Failed to enable video:", e);
        setMediaConstraints((prev) => ({ ...prev, videoEnabled: false }));
      }
    } else {
      const videoTracks = getVideoTracks(stream);
      videoTracks.forEach((t) => {
        t.stop();
        stream.removeTrack(t);
      });
      await producerWebRTC.current!.produceStream(connectionRef.current, stream);
      setLocalStream(new MediaStream(stream.getTracks()));
    }
  }, [mediaConstraints, setMediaConstraints]);

  // Toggle audio.
  //
  // Cheap-mute (matches the group path, useProduceController.onAudioChange):
  // mute flips `track.enabled = false` and unmute flips it back — instant, no
  // getUserMedia + produceStream renegotiation. The old path stopped/removed
  // the track on mute and re-acquired + renegotiated on unmute, which took
  // seconds so the mic came on long after the icon flipped. Keeping the track
  // alive leaves the OS mic indicator lit during mute (accepted tradeoff for
  // instant unmute). We only fall back to a full re-acquire when there is no
  // live audio track (first enable, or the device ended/was unplugged).
  const toggleAudio = useCallback(async () => {
    const newEnabled = !mediaConstraints.audioEnabled;
    setMediaConstraints((prev) => ({ ...prev, audioEnabled: newEnabled }));

    const stream = producerWebRTC.current?.webRTCData?.producer?.stream;
    if (!stream || !connectionRef.current) return;

    const liveAudio = stream.getAudioTracks().filter((t) => t.readyState === "live");

    if (liveAudio.length > 0) {
      // Instant path — just gate the existing track(s).
      liveAudio.forEach((t) => {
        t.enabled = newEnabled;
      });
      return;
    }

    // No live track: either muting with nothing to gate (no-op) or unmuting
    // after the device ended — re-acquire and (re)produce.
    if (!newEnabled) return;
    try {
      let newMedia: MediaStream;
      try {
        newMedia = await navigator.mediaDevices.getUserMedia({ audio: mediaConstraints.audio || true });
      } catch (constraintErr) {
        // Fallback to basic audio if saved constraints are stale (e.g. old deviceId)
        // eslint-disable-next-line no-console
        console.warn("[call] Audio constraints failed, falling back to default:", constraintErr);
        newMedia = await navigator.mediaDevices.getUserMedia({ audio: true });
      }
      // Drop any ended audio track still attached (device unplugged mid-call)
      // before producing the new one — otherwise produceStream keeps the dead
      // track's producer open alongside the fresh one and the peer may consume
      // the silent producer. Mirrors toggleVideo's remove-before-produce.
      stream
        .getAudioTracks()
        .filter((t) => t.readyState !== "live")
        .forEach((t) => stream.removeTrack(t));
      newMedia.getTracks().forEach((t) => acquiredTracksRef.current.push(t));
      addTracksToStream(stream, newMedia.getAudioTracks(), true);
      await producerWebRTC.current!.produceStream(connectionRef.current, stream);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[call] Failed to enable audio:", e);
      setMediaConstraints((prev) => ({ ...prev, audioEnabled: false }));
    }
  }, [mediaConstraints, setMediaConstraints]);

  // End call
  const endCall = useCallback(async () => {
    if (endingRef.current) return;
    endingRef.current = true;
    // eslint-disable-next-line no-console
    console.warn(`[CALL-END-MUTATE] from=endCall callId=${callId} callPhase=${callPhaseRef.current}`);
    // Mark the call ended immediately so the next render disables the
    // reconnect hook (`enabled` depends on `callPhase !== "ended"`).
    // Otherwise a transport teardown triggered by `providers.CallsProvider.end`
    // would race a stray reconnect attempt against the closing transport.
    if (mountedRef.current) {
      setEndReason("ended");
      setCallPhase("ended");
    }
    // Tell the peer first (needs the producer data channel), THEN release the
    // local camera/mic immediately. Previously tracks were only stopped in
    // cleanup() on unmount — so the OS camera/mic indicators stayed lit after
    // leaving the call until the page actually tore down (or was reloaded).
    try {
      await producerWebRTC.current?.webRTCData?.producer?.client?.sendData({
        event: SendDataEvent.PRODUCER_FINISHED_STREAM,
      });
    } catch {
      /* ignore */
    }
    try {
      localStreamRef.current?.getTracks().forEach((t) => t.stop());
    } catch {
      /* ignore */
    }
    try {
      remoteStreamRef.current?.getTracks().forEach((t) => t.stop());
    } catch {
      /* ignore */
    }
    try {
      stopAcquiredMedia();
    } catch {
      /* ignore */
    }
    try {
      producerWebRTC.current?.closeAllStreams();
    } catch {
      /* ignore */
    }
    try {
      consumerWebRTC.current?.closeAllStreams();
    } catch {
      /* ignore */
    }

    try {
      await providers.CallsProvider.end({ callId });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[call] Failed to end call:", e);
    }
  }, [callId, stopAcquiredMedia]);

  // Rebuild producer + consumer without ending the call. Called by
  // useConnectionReconnect when the WebRTC link drops. Closes the old
  // StreamingHelpers, releases the `streamsStartedRef` guard, and re-runs
  // the full produce/consume pipeline. The signalling Call row stays
  // untouched, so the remote peer is still in ACCEPTED and sees the new
  // producer ID appear via the SFU.
  //
  // In-flight guard: a network-recover signal arriving while we're already
  // rebuilding (e.g. browser online + Capacitor networkStatusChange firing
  // back-to-back) would otherwise restart the pipeline mid-flight and
  // produce two concurrent transports. The flag collapses the second call
  // into a no-op; the next ICE-state-change or recovery signal after we
  // finish will pick up if anything went wrong.
  const reconnectingRef = useRef(false);
  const reconnectStreams = useCallback(async () => {
    if (!mountedRef.current || endingRef.current) return;
    if (reconnectingRef.current) return;
    reconnectingRef.current = true;
    try {
      // Stop the previous getUserMedia tracks BEFORE closing the
      // StreamingHelper — `closeAllStreams` tears down the RTCPeerConnection
      // but does NOT release camera/mic hardware. On Safari the next
      // getUserMedia would either hang on "device already in use" or hand
      // back the same MediaStreamTrack, breaking the new producer. Chrome
      // is more forgiving but leaks track objects across reconnect cycles.
      try {
        localStreamRef.current?.getTracks().forEach((t) => t.stop());
      } catch {
        /* ignore */
      }
      try {
        remoteStreamRef.current?.getTracks().forEach((t) => t.stop());
      } catch {
        /* ignore */
      }
      // Release the previous getUserMedia tracks too — startStreams re-acquires
      // fresh ones below, so any orphaned clones from a pre-reconnect toggle
      // must not survive into the new pipeline.
      try {
        stopAcquiredMedia();
      } catch {
        /* ignore */
      }
      try {
        producerWebRTC.current?.closeAllStreams();
      } catch {
        /* ignore */
      }
      try {
        consumerWebRTC.current?.closeAllStreams();
      } catch {
        /* ignore */
      }
      producerWebRTC.current = null;
      consumerWebRTC.current = null;
      setConsumerHelper(null);
      setProducerHelper(null);
      streamsStartedRef.current = false;
      // Use the ref, not a captured closure — `startStreams` is recreated
      // whenever `mediaConstraints` change (camera/mic toggled), and we
      // need the latest version so a reconnect after a toggle respects
      // the user's current camera/mic state.
      await startStreamsRef.current?.();
    } finally {
      reconnectingRef.current = false;
    }
  }, [stopAcquiredMedia]);

  const { connectionStatus } = useConnectionReconnect({
    webRTC: consumerHelper,
    onReconnect: reconnectStreams,
    enabled: !!consumerHelper && callPhase !== "ended" && !endingRef.current,
  });

  // Producer transport health. Symmetric to the consumer above: in 1-on-1
  // each direction can fail independently, and an outgoing-video drop
  // (producer ICE "failed") is invisible to the consumer-side hook. Both
  // hooks call the same `reconnectStreams` — `reconnectingRef` collapses
  // concurrent triggers into a single rebuild.
  useConnectionReconnect({
    webRTC: producerHelper,
    onReconnect: reconnectStreams,
    enabled: !!producerHelper && callPhase !== "ended" && !endingRef.current,
  });

  // Cleanup — use refs to avoid stale closures
  const localStreamRef = useRef(localStream);
  localStreamRef.current = localStream;
  const remoteStreamRef = useRef(remoteStream);
  remoteStreamRef.current = remoteStream;
  const callPhaseRef = useRef(callPhase);
  callPhaseRef.current = callPhase;
  const callRef = useRef(call);
  callRef.current = call;

  const cleanup = useCallback(() => {
    mountedRef.current = false;
    try {
      producerWebRTC.current?.closeAllStreams();
    } catch {
      /* ignore */
    }
    try {
      consumerWebRTC.current?.closeAllStreams();
    } catch {
      /* ignore */
    }
    try {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
    } catch {
      /* ignore */
    }
    try {
      remoteStreamRef.current.getTracks().forEach((t) => t.stop());
    } catch {
      /* ignore */
    }
    try {
      stopAcquiredMedia();
    } catch {
      /* ignore */
    }
    if (statusSubRef.current) {
      try {
        statusSubRef.current.unsubscribe();
      } catch {
        /* ignore */
      }
      statusSubRef.current = null;
    }
    producerWebRTC.current = null;
    consumerWebRTC.current = null;
    setConsumerHelper(null);
  }, [stopAcquiredMedia]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useUnmount(() => {
    // Decide FIRST, tear down LATER. The previous version called
    // cleanup() at the top, which closes producer/consumer transports —
    // and then conditionally skipped end(). Production showed the cost:
    // on a spurious React-level remount (parent re-render with a new key,
    // route reconciliation, etc.) we kept the backend Call row alive but
    // destroyed the underlying mediasoup transports. Six milliseconds
    // later the component re-mounted, built fresh transports against
    // backend keys that the SFU no longer recognised, hit 404 on
    // transportConnect, and the peer's reconnect kicked in. Effectively
    // every 2-3 minutes the call experienced a forced reconnect that
    // users perceived as instability.
    //
    // Evidence the unmount is spurious (so we should preserve the call):
    //   (a) callPhase was "connected" — mediasoup is/was streaming
    //   (b) Call.startTime indicates accepted ≥ 30s ago
    // EITHER piece triggers the skip — (a) covers the canonical bug
    // where call.startTime hasn't landed yet (status subscription race)
    // but the call is clearly live.
    //
    // ONLY "connected" counts. "connecting" is a brief window where
    // startStreams could still reject; skipping there would leak a
    // backend Call row whose peer never even built a StreamingHelper.
    const startTimeStr = callRef.current?.startTime;
    const startMs = startTimeStr ? Date.parse(startTimeStr) : NaN;
    const callAgeMs = Number.isFinite(startMs) ? Date.now() - startMs : 0;
    const wasConnected = callPhaseRef.current === "connected";
    const callIsActive = callPhaseRef.current !== "ended" && !endingRef.current;
    const shouldSkip = callIsActive && (wasConnected || callAgeMs > 30000);

    // Diagnostic-only: pair with the MOUNT log above so a spurious React
    // remount is obvious in the dump (instance#N unmount followed shortly
    // by instance#N+1 mount, same callId).
    // eslint-disable-next-line no-console
    console.warn(
      `[PAGE-LIFECYCLE] useCallController UNMOUNT instance#${instanceIdRef.current} callId=${callId} callPhase=${
        callPhaseRef.current
      } callAge=${callAgeMs}ms shouldSkip=${shouldSkip} visibility=${
        typeof document !== "undefined" ? document.visibilityState : "?"
      } hasFocus=${typeof document !== "undefined" ? document.hasFocus() : "?"} online=${
        typeof navigator !== "undefined" ? navigator.onLine : "?"
      } t=${Math.round(performance.now())}ms`
    );

    if (shouldSkip) {
      // PRESERVE the transports too. Without this the spurious remount
      // immediately gets dead SFU keys. Cost we accept: if this unmount
      // is REAL (user closed the tab), the producer/consumer helpers
      // leak in JS memory until GC, and the camera/mic LED stays lit
      // until the browser tears down the page. That's bounded and
      // recoverable; killing the live call is not.
      // eslint-disable-next-line no-console
      console.warn(
        `[CALL-END-MUTATE] from=useUnmount SKIPPED callId=${callId} callPhase=${callPhaseRef.current} callAge=${callAgeMs}ms — preserving transports for likely remount`
      );
      mountedRef.current = false;
      return;
    }

    cleanup();
    if (callIsActive) {
      // eslint-disable-next-line no-console
      console.warn(
        `[CALL-END-MUTATE] from=useUnmount callId=${callId} callPhase=${callPhaseRef.current} endingRef=${endingRef.current} callAge=${callAgeMs}ms`
      );
      providers.CallsProvider.end({ callId }).catch(() => {});
    }
  });

  // Release local camera/mic the instant the call ends — covers a local hang-up
  // (endCall), the peer ending it, AND a server-driven end (CallsProvider.end →
  // SFU closes the transport remotely) where endCall() never ran its teardown.
  // mediasoup produces with stopTracks:false, so a remote transport close does
  // NOT stop the device tracks — only an explicit stop releases the OS device.
  // closeAllStreams() stops the producer.stream tracks (the same device track
  // objects as localStream); the per-stream stops are belt-and-suspenders.
  // All idempotent with endCall()/cleanup(); reconnect is already disabled at
  // callPhase==="ended", so closing transports here can't trigger a rebuild.
  useEffect(() => {
    if (callPhase !== "ended") return;
    const localTracks = localStreamRef.current?.getTracks() ?? [];
    const remoteTracks = remoteStreamRef.current?.getTracks() ?? [];
    const acquiredCount = acquiredTracksRef.current.length;
    localTracks.forEach((t) => {
      try {
        t.stop();
      } catch {
        /* ignore */
      }
    });
    remoteTracks.forEach((t) => {
      try {
        t.stop();
      } catch {
        /* ignore */
      }
    });
    // Stop ALL acquired device tracks — this is what releases a camera/mic that
    // was cloned into the producer stream by a toggle (the clone is in
    // localStream above; the orphaned original is only reachable here).
    try {
      stopAcquiredMedia();
    } catch {
      /* ignore */
    }
    try {
      producerWebRTC.current?.closeAllStreams();
    } catch {
      /* ignore */
    }
    try {
      consumerWebRTC.current?.closeAllStreams();
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line no-console
    console.info(
      `[call] ended → released local media (${localTracks.length} produced + ${acquiredCount} acquired track(s) stopped)`
    );
  }, [callPhase, stopAcquiredMedia]);

  // Tracks how many times we've successfully started streams. Bumping this
  // gives downstream effects a stable signal to (re)subscribe to data-channel
  // events on the consumer client, which is a ref and otherwise opaque to
  // dependency arrays.
  const [streamsStarted, setStreamsStarted] = useState(0);
  useEffect(() => {
    if (callPhase === "connected") setStreamsStarted((n) => n + 1);
  }, [callPhase]);

  return {
    call,
    callPhase,
    endReason,
    localStream,
    remoteStream,
    mediaConstraints,
    toggleVideo,
    toggleAudio,
    endCall,
    producerWebRTC: producerWebRTC.current,
    consumerWebRTC: consumerWebRTC.current,
    streamsStarted,
    connectionStatus,
  };
}
